import utils from './utils';
import log from './log';

let nslToken = '';

const DEFAULT_NSL_TOKEN_TTL_MS = 30 * 60 * 1000;
/** Parent `SpaAuth.refreshAccessToken` issues a new token only after the current one expires. */
const REFRESH_AFTER_EXPIRY_MS = 500;
const RETRY_AFTER_EXPIRY_MS = 1_000;
const REFRESH_REQUEST_TIMEOUT_MS = 30 * 1000;

/** JWT payload fields used across NSL token handling. */
export type JwtPayload = {
  exp?: number;
  iat?: number;
  sub?: string;
  tenant_id?: string;
  NortonGUID?: string;
};

type PendingRefresh = {
  reject: (_error: Error) => void;
  resolve: (_token: string) => void;
};

type SpaAuthBridge = {
  refreshAccessToken: (_callback: (_token: string) => void) => void;
};

let refreshTimerId: ReturnType<typeof setTimeout> | null = null;
let pendingRefresh: PendingRefresh | null = null;

/**
 * Sets nslToken for use in methods requiring NSL tokens.
 * Should be set anytime a new NSL token is fetched.
 *
 * **Partner white-label flows** (`PARTNER_LOGIN`, `PARTNER_EXTEND_ACCESS`) must use this
 * helper only — the partner parent owns NSL refresh and pushes updated tokens via
 * `PARTNER_EXTEND_ACCESS`. Do not call `updateNslToken` for partner auth.
 *
 * @param token NSL token string
 */
export function setNslToken(token: string): void {
  nslToken = token;
}

/**
 * Returns the nsl token
 * @returns Nsl token
 */
export function getNslToken(): string {
  return nslToken;
}

/**
 * Best-effort decode of a JWT payload (no signature verification).
 * Used client-side only to read claims such as `exp`, `sub`, or `tenant_id`.
 */
export function parseJwtPayload(token: string): JwtPayload | null {
  const segments = token.split('.');
  if (segments.length < 2) {
    return null;
  }

  try {
    const base64 = segments[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded)) as JwtPayload;
  } catch {
    return null;
  }
}

/**
 * Returns the token expiry instant in milliseconds since epoch.
 * Prefers the `exp` claim; falls back to `iat + fallbackTtlMs`, then `now + fallbackTtlMs`.
 */
export function getJwtExpiryMs(token: string, fallbackTtlMs = DEFAULT_NSL_TOKEN_TTL_MS): number {
  const payload = parseJwtPayload(token);
  const nowMs = Date.now();

  if (typeof payload?.exp === 'number' && Number.isFinite(payload.exp)) {
    return payload.exp * 1000;
  }
  if (typeof payload?.iat === 'number' && Number.isFinite(payload.iat)) {
    return payload.iat * 1000 + fallbackTtlMs;
  }
  return nowMs + fallbackTtlMs;
}

function clearRefreshTimer(): void {
  if (refreshTimerId !== null) {
    clearTimeout(refreshTimerId);
    refreshTimerId = null;
  }
}

/** Cancels any scheduled refresh and in-flight parent refresh request. */
export function stopNslTokenRefresh(): void {
  clearRefreshTimer();
  if (pendingRefresh) {
    pendingRefresh.reject(new Error('NSL token refresh cancelled'));
    pendingRefresh = null;
  }
}

/**
 * Stores the token and schedules post-expiry refresh for Norton NGP iframe flows only
 * (`REACT_APP_ENABLE_OIDC_TOKEN_EXCHANGE` + parent `SpaAuth.refreshAccessToken`).
 *
 * Not for partner auth — partners use `setNslToken` and `PARTNER_EXTEND_ACCESS` instead.
 */
export function updateNslToken(token: string): void {
  setNslToken(token);
  if (!token) {
    stopNslTokenRefresh();
    return;
  }
  if (typeof window !== 'undefined' && window.REACT_APP_ENABLE_OIDC_TOKEN_EXCHANGE === true) {
    scheduleNslTokenRefresh(token);
  }
}

/**
 * Schedules `SpaAuth.refreshAccessToken` after the JWT expires.
 * @param offsetAfterExpiryMs Milliseconds after `exp` to trigger refresh (default 500ms).
 */
export function scheduleNslTokenRefresh(token: string, offsetAfterExpiryMs = REFRESH_AFTER_EXPIRY_MS): void {
  clearRefreshTimer();
  if (!token) {
    return;
  }

  const expiryMs = getJwtExpiryMs(token);
  const refreshAtMs = expiryMs + offsetAfterExpiryMs;
  const delayMs = Math.max(0, refreshAtMs - Date.now());

  refreshTimerId = setTimeout(() => {
    refreshTimerId = null;
    void runNslTokenRefresh(token, offsetAfterExpiryMs);
  }, delayMs);
}

export function resolvePendingNslTokenRefresh(token: string): void {
  pendingRefresh?.resolve(token);
}

export function rejectPendingNslTokenRefresh(error: Error): void {
  pendingRefresh?.reject(error);
}

function tryParentSpaAuthRefresh(): Promise<string | null> {
  return new Promise((resolve) => {
    try {
      const parentWindow = window.parent as Window & {SpaAuth?: SpaAuthBridge};
      if (!parentWindow?.SpaAuth || typeof parentWindow.SpaAuth.refreshAccessToken !== 'function') {
        resolve(null);
        return;
      }
      parentWindow.SpaAuth.refreshAccessToken((token: string) => {
        resolve(token || null);
      });
    } catch {
      resolve(null);
    }
  });
}

function requestNslAccessTokenRefreshViaPostMessage(): Promise<string> {
  return new Promise((resolve, reject) => {
    if (pendingRefresh) {
      reject(new Error('NSL token refresh already in progress'));
      return;
    }

    const timeoutId = setTimeout(() => {
      pendingRefresh = null;
      reject(new Error('NSL token refresh timed out'));
    }, REFRESH_REQUEST_TIMEOUT_MS);

    pendingRefresh = {
      resolve: (token: string) => {
        clearTimeout(timeoutId);
        pendingRefresh = null;
        resolve(token);
      },
      reject: (error: Error) => {
        clearTimeout(timeoutId);
        pendingRefresh = null;
        reject(error);
      },
    };

    utils.sendMessage({type: 'REFRESH_NSL_ACCESS_TOKEN', payload: {}}, window.REACT_APP_PARENT_HOST);
  });
}

async function requestNslAccessTokenRefresh(): Promise<string> {
  const directToken = await tryParentSpaAuthRefresh();
  if (directToken) {
    return directToken;
  }
  return requestNslAccessTokenRefreshViaPostMessage();
}

async function runNslTokenRefresh(expectedToken: string, attemptOffsetAfterExpiryMs: number): Promise<void> {
  const oldToken = getNslToken();
  try {
    const newToken = await requestNslAccessTokenRefresh();

    if (!newToken) {
      throw new Error('NSL token refresh returned empty token');
    }

    if (newToken === oldToken || newToken === expectedToken) {
      if (attemptOffsetAfterExpiryMs < RETRY_AFTER_EXPIRY_MS) {
        scheduleNslTokenRefresh(oldToken, RETRY_AFTER_EXPIRY_MS);
      }
      return;
    }

    setNslToken(newToken);
    scheduleNslTokenRefresh(newToken, REFRESH_AFTER_EXPIRY_MS);
  } catch (error) {
    log.error('[NSL token refresh] failed:', error);
  }
}
